import { useRef, useState } from 'react'
import { invitePerson, sendSignInCode } from '../../lib/invites'
import type { OrgUnitOption } from '../../lib/admin'
import { messageOf } from '../../lib/errors'
import { SUPPORTED_LOCALES } from '../../lib/locale'

/**
 * Invite somebody who is not on the roster yet.
 *
 * ── TWO STEPS, SHOWN AS TWO STEPS ───────────────────────────────────────────
 *
 * Creating the person and sending them a code are separate acts (045), and the
 * form says so rather than hiding it behind one button labelled "Invite". That
 * is not pedantry: the row is what lets them in and it persists; the code
 * expires and can be re-sent forever. Somebody who believes one button did both
 * will re-run the whole thing when the code lapses and hit "that address
 * already belongs to somebody on the roster", which reads like a bug.
 *
 * So the two acts are two BUTTONS. Sending is the primary because it is what an
 * administrator almost always wants; "Add without sending" exists because
 * during the roster load it very much is not — adding 900 rows must not fire
 * 900 emails.
 *
 * ── WHAT ARRIVES IS NOT A WELCOME ───────────────────────────────────────────
 *
 * The code is a bare six-digit number with no mention of ConeTec or the hub
 * whichever transport is sending it; FND-14 is what makes it a welcome. The
 * form says so where somebody is about to send one, because the alternative is
 * an administrator hearing it from a confused colleague.
 *
 * ── IN A DIALOG SINCE 137 ───────────────────────────────────────────────────
 *
 * The "Invite someone" button was a full-width primary bar across the top of
 * the roster, and opening it pushed the table down by the height of the form.
 * The button is now a normal-sized one at the top right of the page and this
 * form opens in a Dialog (Admin.tsx). The outcome sentence goes back to the
 * page through `onInvited`, because the dialog closes on success.
 */
export function InvitePerson({
  orgUnits,
  onInvited,
  onCancel,
}: {
  orgUnits: OrgUnitOption[]
  /** Called after a successful add, with the sentence to show on the page. */
  onInvited: (outcome: string) => void | Promise<void>
  onCancel: () => void
}) {
  const [displayName, setDisplayName] = useState('')
  const [email, setEmail] = useState('')
  const [locale, setLocale] = useState('en-CA')
  const [orgUnitId, setOrgUnitId] = useState('')
  /**
   * Which button was pressed, in a REF rather than state.
   *
   * setState is asynchronous, so `onClick={() => setSendNow(false)}` followed
   * by a submit handler reading `sendNow` reads the PREVIOUS value — the
   * quiet button would have sent a code on its first press and then behaved
   * correctly forever after, which is the kind of bug that survives testing.
   */
  const sendRef = useRef(true)
  const [busy, setBusy] = useState<'send' | 'quiet' | null>(null)
  const [error, setError] = useState<string | null>(null)

  function reset() {
    setDisplayName('')
    setEmail('')
    setLocale('en-CA')
    setOrgUnitId('')
    setError(null)
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    if (busy) return
    const sendNow = sendRef.current
    setBusy(sendNow ? 'send' : 'quiet')
    setError(null)
    try {
      const personId = await invitePerson({
        displayName,
        email,
        locale,
        orgUnitId: orgUnitId || null,
      })
      const name = displayName.trim()
      let outcome: string
      if (sendNow) {
        // Deliberately not rolled back if this fails. The person exists and can
        // be sent a code later; unwinding a real roster row because an email
        // bounced would lose the more valuable half of the work.
        try {
          await sendSignInCode(personId, email)
          outcome = `${name} was added and sent a code.`
        } catch (sendErr) {
          outcome = `${name} was added, but the code did not send: ${messageOf(
            sendErr,
          )}. Resend it from their record.`
        }
      } else {
        outcome = `${name} was added. They cannot sign in until a code is sent.`
      }
      reset()
      await onInvited(outcome)
    } catch (err) {
      setError(messageOf(err))
    } finally {
      setBusy(null)
    }
  }

  return (
    <form className="admin__invite" onSubmit={submit}>

      <div className="admin__fields">
        <label className="admin__field">
          <span className="admin__searchlabel">Name</span>
          <input
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="Jane Doe"
            required
            autoComplete="off"
          />
        </label>

        <label className="admin__field">
          <span className="admin__searchlabel">Work email</span>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="jane.doe@conetec.com"
            required
            autoComplete="off"
          />
        </label>

        <label className="admin__field">
          <span className="admin__searchlabel">Language</span>
          <select value={locale} onChange={(e) => setLocale(e.target.value)}>
            {/* 113. The one list of languages a person can be set to — never
                a second, hand-typed one that could offer what the database
                refuses. */}
            {SUPPORTED_LOCALES.map((l) => (
              <option key={l.key} value={l.key}>{l.label}</option>
            ))}
          </select>
        </label>

        <label className="admin__field">
          <span className="admin__searchlabel">Where (optional)</span>
          <select value={orgUnitId} onChange={(e) => setOrgUnitId(e.target.value)}>
            <option value="">Not placed yet</option>
            {orgUnits.map((o) => (
              <option key={o.orgUnitId} value={o.orgUnitId}>
                {'  '.repeat(o.depth)}
                {o.name}
              </option>
            ))}
          </select>
        </label>
      </div>

      {error && (
        <p className="roles__error" role="alert">
          {error}
        </p>
      )}

      {/* TWO BUTTONS, NOT A CHECKBOX AND ONE BUTTON.
          A checkbox states a preference about a button you have not pressed
          yet, so the form had two controls for one decision and you had to read
          both to know what pressing would do. What happens is now written on
          the thing you press — the same move that turned Wiki One's curator
          checkbox into a ModeSwitch.

          Sending is the primary because it is what an administrator almost
          always wants; adding without sending is the quiet one and exists for
          the roster load, where 900 rows must not fire 900 emails. */}
      <div className="admin__inviteactions">
        <button
          type="submit"
          className="btn btn--primary btn--sm"
          disabled={busy !== null}
          onClick={() => { sendRef.current = true }}
        >
          {busy === 'send' ? 'Adding…' : 'Add and send a code'}
        </button>
        <button
          type="submit"
          className="btn btn--quiet"
          disabled={busy !== null}
          onClick={() => { sendRef.current = false }}
        >
          {busy === 'quiet' ? 'Adding…' : 'Add without sending'}
        </button>
        <button
          type="button"
          className="btn btn--quiet"
          disabled={busy !== null}
          onClick={() => {
            reset()
            onCancel()
          }}
        >
          Cancel
        </button>
      </div>

      <p className="admin__invitenote">
        The code is a plain six-digit number with nothing in it about Conetec or
        the hub, so tell them it is coming. If sending fails they are still
        added, and it can be re-sent from their record.
      </p>
    </form>
  )
}
